import { useCallback, useEffect, useState } from "react";
import { GetInvoiceAll, GetInvoiceComPDF, SendPayloadContifico } from "../../../Service/Service.billing";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { getDocumentoContifico } from "../../../Service/Service.ContificoAPI";
import EliminarFactura from "./EliminarFactura";
import DetalleFactura from "./DetalleFactura";
import { puedeVerRide, puedeEnviarContifico, tieneErroresContifico, puedeAnular } from "../../../utils/facturaAcciones";
import usePermissions from "../../../hook/usePermissions";
import DataTable from "../../UI/DataTable/DataTable";
import { Celda, CeldaDoble, tituloDeNombre } from "../../UI/DataTable/piezas";
import { ChipEstado, ChipCategoria } from "../../UI/Chip";
import Boton from "../../UI/Boton";
import MenuAcciones from "../../UI/MenuAcciones";
import Modal from "../../UI/Modal/Modal";
import { useToast } from "../../UI/Aviso";
import { IconoVer, IconoDescargar, IconoError, IconoHistorial, IconoEliminar } from "../../UI/Iconos";

// SISTEMA-DE-DISENO · pantalla migrada al kit (3ª).
//
// MIGRAR NO ES REDISEÑAR: `GetInvoiceAll`, `SendPayloadContifico`,
// `getDocumentoContifico`, `GetInvoiceComPDF`, los helpers canónicos de
// `utils/facturaAcciones.js` y los permisos son EXACTAMENTE los de antes,
// incluidos el flag de QA de Contífico y el truco del `window.open` síncrono
// para el RIDE. Se conservan sus comentarios: explican decisiones que costaron
// caro y siguen vigentes.
//
// Lo que cambia:
//   · 10 columnas -> 5. Nada se pierde: el tipo y la fecha viajan con el
//     número de documento; subtotal e IVA, bajo el total; los dos estados
//     comparten celda.
//   · Seis iconos sueltos por fila -> una acción principal visible ("Ver") y
//     el resto en el menú de tres puntos, que es para lo secundario.
//   · Los cinco `Swal` de esta pantalla pasan al Modal y a los toasts del kit.
//   · La tabla gana su estado de ERROR: antes, si `GetInvoiceAll` fallaba, la
//     pantalla se quedaba en blanco sin decir nada.
function GridFacturaListado({ data }) {
  // RBAC-CUTOVER-04: "Reenviar a Contífico" y "Eliminar" (anular) dependen
  // de billing.contifico_send/billing.cancel reales.
  const { hasPermission } = usePermissions();
  const { mostrar } = useToast();

  const { filters, onTotal } = data;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resendingInvoiceId, setResendingInvoiceId] = useState(null);
  const [invoiceList, setinvoiceList] = useState([]);
  const [showDeleteInvoice, setShowDeleteInvoice] = useState(false);
  const [showDetalleInvoice, setShowDetalleInvoice] = useState(false);
  const [detalleInvoice, setDetalleInvoice] = useState(null);

  const [dataInvoice, setDataInvoice] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // Diálogos del kit: sustituyen a los `Swal` de esta pantalla. `confirmarEnvio`
  // guarda la factura sobre la que se pidió confirmación; `mensaje` es el aviso
  // de sólo lectura (errores de Contífico / comentario de anulación).
  const [confirmarEnvio, setConfirmarEnvio] = useState(null);
  const [mensaje, setMensaje] = useState(null);

  const fetchInitialData = useCallback(
    async (page = 1, limit = 10, filtros) => {
      setLoading(true);
      setError(null);
      const result = await GetInvoiceAll({ page, limit, filters: filtros });

      if (result && result.success) {
        const fetchInvoice = result.data.map((item, index) => ({
          ...item,
          key: item.idfactura,
          index,
        }));
        setinvoiceList(fetchInvoice);
        setTotal(result.pagination.total);
        setCurrentPage(result.pagination.page);
        setPageSize(result.pagination.limit);
        if (onTotal) onTotal(result.pagination.total);
      } else {
        // ESTADO DE ERROR: antes este caso no existía y la pantalla quedaba en
        // blanco, indistinguible de "no hay facturas".
        setinvoiceList([]);
        setTotal(0);
        setError(result?.message || "No se pudo obtener el listado de facturas.");
      }

      setLoading(false);
    },
    [onTotal]
  );

  // El envío real a Contífico: idéntico al de antes (payload, flag de QA,
  // `already_sent`). Sólo cambia cómo se pide la confirmación y cómo se informa.
  const ejecutarEnvio = async (record) => {
    const { idfactura, num_documento, documento } = record;
    setConfirmarEnvio(null);
    try {
      setLoading(true);
      setResendingInvoiceId(idfactura);

      const payload = idfactura ? { idfactura } : { num_documento, documento };

      // CONTIFICO-LOCAL-QA-GUARD-01 (TEMPORAL, default-OFF): mismo flag que
      // ConfirmBilling.js -- este es el otro punto de entrada (manual,
      // "Reenviar a Contífico" desde el listado) que puede disparar un envio
      // real. Con el flag ON no se llama a SendPayloadContifico; el backend
      // (DISABLE_CONTIFICO_SEND) igual lo bloquearia si llegara a llamarse,
      // pero se corta aca tambien para no confundir al QA con un round-trip
      // de red innecesario.
      const skipContificoSend = process.env.REACT_APP_SKIP_CONTIFICO_SEND === "true";

      const envio = skipContificoSend
        ? {
            success: true,
            data: { qa_skipped: true },
            message: "[QA local] Envío a Contífico deshabilitado (REACT_APP_SKIP_CONTIFICO_SEND=true).",
          }
        : await SendPayloadContifico(payload);
      if (!envio?.success) throw new Error(envio?.message || "Error de Contífico");

      const alreadySent = envio?.data?.already_sent;

      mostrar({
        tipo: skipContificoSend ? "info" : "exito",
        texto:
          envio?.message ||
          (alreadySent ? "La factura ya tenía id Contífico." : "Factura enviada a Contífico correctamente."),
      });

      fetchInitialData(currentPage, pageSize, filters);
    } catch (e) {
      mostrar({ tipo: "error", texto: e?.message || "Error al reenviar la factura" });
    } finally {
      setLoading(false);
      setResendingInvoiceId(null);
    }
  };

  const handleShowDetalle = (record) => {
    setDetalleInvoice(record);
    setShowDetalleInvoice(true);
  };

  const handleCloseDetalle = () => {
    setShowDetalleInvoice(false);
  };

  // Aviso de sólo lectura. El contenido es el MISMO que mostraba el Swal: el
  // comentario de anulación si la factura está anulada, o el mensaje de error
  // de Contífico. Ya no se inyecta HTML: es texto.
  const handleShowErrors = (record) => {
    const anulada = record.estado === "I" || record.estado === "Eliminado";
    setMensaje({
      titulo: anulada ? "Factura anulada" : "Errores en Contífico",
      texto: anulada
        ? record.comentario_anular || "No hay comentario de anulación registrado."
        : record.mensaje_contifico || "No hay errores registrados.",
      tono: anulada ? "anulado" : "proceso",
    });
  };

  const handleRIDE = async (record) => {
    // RIDE-POPUP-FIX-08:
    // 1) Abrimos la pestaña de forma SÍNCRONA dentro del gesto del click.
    //    Si abriéramos después del `await getDocumentoContifico`, el
    //    window.open queda fuera del evento de usuario y algunos
    //    navegadores lo bloquean legítimamente.
    // 2) NO usamos el flag "noopener" en window.open: con ese flag la
    //    función devuelve `null` AUNQUE la apertura tenga éxito, y esa
    //    era la causa del falso "El navegador bloqueó la ventana."
    //    Anulamos `opener` manualmente (about:blank es same-origin).
    const nuevaPestana = window.open("", "_blank");
    if (!nuevaPestana) {
      mostrar({ tipo: "aviso", texto: "Permita ventanas emergentes para este sitio e intente nuevamente." });
      return;
    }
    try {
      nuevaPestana.opener = null;
    } catch (_) {
      /* noop */
    }

    try {
      setLoading(true);
      const respuesta = await getDocumentoContifico(record.id_contifico);
      if (!respuesta.success || !respuesta?.data?.url_ride) {
        throw new Error(respuesta.mensaje || "No se obtuvo la URL del RIDE.");
      }
      nuevaPestana.location = respuesta.data.url_ride;
    } catch (e) {
      try {
        nuevaPestana.close();
      } catch (_) {
        /* noop */
      }
      mostrar({ tipo: "error", texto: e?.message || "No se pudo abrir el RIDE." });
    } finally {
      setLoading(false);
    }
  };

  const handleCOM = async (record) => {
    try {
      setLoading(true);
      const response = await GetInvoiceComPDF(record.num_documento);
      const blob = response.data;
      const objectURL = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectURL;
      link.download = "COMPROBANTE.pdf";
      link.click();
      URL.revokeObjectURL(objectURL);
    } catch (e) {
      mostrar({ tipo: "error", texto: e?.message || "No se pudo descargar el comprobante." });
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = () => {
    fetchInitialData(currentPage, pageSize, filters);
  };

  const handleCloseDelete = () => {
    setShowDeleteInvoice(!showDeleteInvoice);
  };

  const handleModalDelete = (record) => {
    setDataInvoice(record);
    setShowDeleteInvoice(!showDeleteInvoice);
  };

  const receptorDe = (r) =>
    r.paciente_factura?.nombres_completos ||
    r.cliente_factura?.nombre ||
    (r.seguros?.nombre ? `${r.seguros.nombre} (aseguradora)` : null);

  const columnas = [
    {
      // Número de documento con su tipo y su fecha debajo: tres columnas de
      // antes que se leen juntas.
      key: "num_documento",
      titulo: "Comprobante",
      ancho: 190,
      render: (r) => (
        <CeldaDoble
          principal={r.num_documento}
          secundario={`${r.documento || "—"} · ${
            r.fecha_emision ? format(new Date(r.fecha_emision), "dd/MM/yyyy", { locale: es }) : "—"
          }`}
          monoPrincipal
        />
      ),
    },
    {
      key: "receptor",
      titulo: "Receptor",
      render: (r) => {
        const receptor = receptorDe(r);
        // Sólo se aplica Title Case a nombres de persona; la razón social de
        // una aseguradora o un cliente se respeta tal cual.
        return <Celda valor={r.paciente_factura?.nombres_completos ? tituloDeNombre(receptor) : receptor} />;
      },
    },
    {
      // Total arriba; el desglose que lo compone, debajo. Ningún dato se pierde.
      key: "total",
      titulo: "Importe",
      ancho: 160,
      alinear: "derecha",
      render: (r) => (
        <CeldaDoble
          principal={Number(r.total ?? 0).toFixed(2)}
          secundario={`Sub ${Number(r.subtotal ?? 0).toFixed(2)} · IVA ${Number(r.iva ?? 0).toFixed(2)}`}
          monoPrincipal
        />
      ),
    },
    {
      key: "estado",
      titulo: "Estado",
      ancho: 190,
      render: (r) => (
        <span style={{ display: "inline-flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
          <ChipEstado tono={r.estado === "A" ? "hecho" : "anulado"}>{r.estado === "A" ? "Activa" : "Anulada"}</ChipEstado>
          {r.id_contifico ? (
            <ChipCategoria tono="info">En Contífico</ChipCategoria>
          ) : (
            // No registrada en Contífico es un dato incompleto, no un estado
            // más: ámbar y con icono.
            <ChipCategoria incompleto title={r.mensaje_contifico || undefined}>
              No registrada
            </ChipCategoria>
          )}
        </span>
      ),
    },
    {
      key: "accion",
      titulo: "",
      ancho: 110,
      alinear: "derecha",
      render: (record) => {
        // FACTURACION-ACCIONES-CONTIFICO-09: las acciones se derivan de helpers
        // canónicos (utils/facturaAcciones.js), no de condiciones sueltas.
        //   - "Ver errores" sólo si hay error real persistido.
        //   - "Enviar/Reenviar" y "Ver RIDE" son MUTUAMENTE EXCLUYENTES: la
        //     primera exige NO registrada, la segunda exige registrada.
        const isActiva = record.estado === "A";
        const isCOM = record.documento === "COM";
        const verErrores = isActiva && tieneErroresContifico(record);
        const enviarContifico = puedeEnviarContifico(record);
        const verRide = puedeVerRide(record);
        const enviando = resendingInvoiceId === record.idfactura;

        // La acción principal es un botón visible; el menú guarda el resto.
        const secundarias = [];
        if (!isActiva) {
          secundarias.push({
            key: "anulacion",
            label: "Ver comentario de anulación",
            icono: IconoError,
            onClick: () => handleShowErrors(record),
          });
        } else {
          if (isCOM) {
            secundarias.push({ key: "com", label: "Descargar COM", icono: IconoDescargar, onClick: () => handleCOM(record) });
          }
          if (verErrores) {
            secundarias.push({ key: "errores", label: "Ver errores", icono: IconoError, onClick: () => handleShowErrors(record) });
          }
          if (enviarContifico && hasPermission("billing.contifico_send")) {
            secundarias.push({
              key: "contifico",
              label: enviando ? "Enviando…" : verErrores ? "Reenviar a Contífico" : "Enviar a Contífico",
              icono: IconoHistorial,
              disabled: enviando,
              onClick: () => setConfirmarEnvio(record),
            });
          }
          if (verRide) {
            secundarias.push({ key: "ride", label: "Ver RIDE", icono: IconoDescargar, onClick: () => handleRIDE(record) });
          }
          if (puedeAnular(record) && hasPermission("billing.cancel")) {
            secundarias.push({ separador: true });
            secundarias.push({
              key: "anular",
              label: "Anular factura",
              icono: IconoEliminar,
              peligro: true,
              onClick: () => handleModalDelete(record),
            });
          }
        }

        return (
          <span className="ac-celda-accion">
            <Boton
              tamano="sm"
              soloIcono
              icono={IconoVer}
              titulo="Ver detalle"
              onClick={(e) => {
                e.stopPropagation();
                handleShowDetalle(record);
              }}
              data-testid={`factura-ver-${record.idfactura}`}
            />
            <MenuAcciones acciones={secundarias} titulo={`Más acciones de la factura ${record.num_documento || record.idfactura}`} />
          </span>
        );
      },
    },
  ];

  useEffect(() => {
    fetchInitialData(currentPage, pageSize, filters);
  }, [fetchInitialData, filters, currentPage, pageSize]);

  const hayFiltros = Object.values(filters || {}).some((v) => v !== null && v !== undefined && v !== "");

  return (
    <>
      <DataTable
        columnas={columnas}
        filas={invoiceList}
        filaKey="idfactura"
        cargando={loading}
        error={error}
        onReintentar={() => fetchInitialData(currentPage, pageSize, filters)}
        onFilaClick={handleShowDetalle}
        etiquetaTabla="Facturas"
        hayFiltrosAplicados={hayFiltros}
        textoVacioSinFiltros="Todavía no hay facturas emitidas."
        paginacion={{
          page: currentPage,
          pageSize,
          total,
          tamanos: [10, 20, 50, 100],
          onCambio: (page, limit) => fetchInitialData(page, limit, filters),
        }}
      />

      {/* Confirmación del envío a Contífico: dice exactamente qué se va a
          enviar, como hacía el Swal, pero con el diálogo del sistema. */}
      <Modal
        abierto={!!confirmarEnvio}
        titulo="¿Reenviar a Contífico?"
        ancho="confirmacion"
        onCerrar={() => setConfirmarEnvio(null)}
        secundario={{ label: "Cancelar" }}
        primario={{
          label: "Reenviar",
          onClick: () => ejecutarEnvio(confirmarEnvio),
          cargando: resendingInvoiceId === confirmarEnvio?.idfactura,
        }}
      >
        <p>
          Se enviará la factura <strong>{confirmarEnvio?.num_documento || "—"}</strong> (id {confirmarEnvio?.idfactura}) a
          Contífico. El backend actualizará el registro local con la respuesta.
        </p>
      </Modal>

      <Modal
        abierto={!!mensaje}
        titulo={mensaje?.titulo || ""}
        ancho="aviso"
        onCerrar={() => setMensaje(null)}
        primario={{ label: "Cerrar", onClick: () => setMensaje(null), icono: false }}
      >
        <p data-testid="factura-mensaje">{mensaje?.texto}</p>
      </Modal>

      <EliminarFactura
        data={{
          show: showDeleteInvoice,
          handleClose: handleCloseDelete,
          invoice: dataInvoice,
          handleConfirmDelete: handleConfirmDelete,
        }}
      />

      <DetalleFactura
        data={{
          show: showDetalleInvoice,
          handleClose: handleCloseDetalle,
          invoice: detalleInvoice,
        }}
      />
    </>
  );
}

export default GridFacturaListado;
